// WorkWithUs.jsx - The affiliate programme. Somebody sends us a seller, we pay
// them for it.
//
// Two audiences arrive here and they want different things. A shop owner wants
// to know what goes on their counter, what it costs them, and when they get
// paid. Somebody with an audience online wants a link. The page is ordered for
// the first, because the display stand is the part that actually works: a person
// clearing out a relative's house talks to an estate dealer or a jeweller long
// before they think to search for a mail-in gold buyer.
//
// Two things this page must not do:
//
//   Never type a figure. The commission, the cap, the starter set and the
//   restock price are all set on the settings screen and read through
//   lib/partners.js, so this page cannot quote a number the business has
//   stopped honouring. The rate used to be left off this page entirely; it is
//   published now, and it is published from settings or not at all.
//
//   Never imply the referrer values, handles or holds anybody's metal. They
//   hand over a kit and we do the rest. That distinction is what keeps this a
//   referral arrangement rather than them needing a dealer licence of their own.
//
// The form posts to the ordinary contact endpoint with a composed message, so
// there is no new model, no new route and no second inbox to remember to read.
// If this ever needs its own record with its own status, that is the moment to
// give it one, and not before.
//
// LEGEND
//   0. partners    what a partner is given and paid, from lib/partners.js
//   1. waysFor()   the two ways somebody can refer
//   2. SUITS       who this actually works for
//   3. TERMS       what we will and will not do, said plainly
//   4. ApplyForm   the enquiry, on top of the contact endpoint
//   5. WorkWithUs  the page

import { useState } from "react";
import { Link } from "react-router";

import { Seo, JsonLd, breadcrumbSchema } from "../lib/seo.jsx";
import { useAsync, useForm } from "../lib/hooks.js";
import { publicApi } from "../lib/api.js";
import { KIT } from "../lib/media.js";
import { partnerTerms, rateLine, price, money } from "../lib/partners.js";
import { Input, Textarea, Select, Button, Alert, Panel } from "../ui/kit.jsx";
import { ArrowRight, Check, Shield, Dollar, Grid, Truck, Note } from "../ui/icons.jsx";
import { Reveal, StaggerBoxed } from "../ui/motion.jsx";
import { PageHead, Figure, CtaBand } from "./parts.jsx";

////////// 0. WHAT A PARTNER IS GIVEN, AND PAID //////////
// Four figures, all from settings through lib/partners.js, which also holds the
// fallbacks the prerendered HTML carries. Nothing on this page types a number.

////////// 1. THE TWO WAYS //////////

const waysFor = (supply) => [
  {
    icon: Grid,
    title: "On your counter",
    body: `We send you the display, ${supply.startingMailers} mailers and the booklets, free. A customer takes a mailer, posts their items in their own time, and the mailer is what tells us it came from you. When you run out, more mailers are ${price(supply.mailerPrice)} each.`,
    aside: "The one that works. Most people are not searching for us when they decide to sell.",
  },
  {
    icon: ArrowRight,
    title: "Through a link",
    body: "A tracked link for a site, a newsletter or a video description. It works the same way and pays the same way, and you can use both.",
    aside: "Also yours: the free charts and calculators on the embed page.",
  },
];

////////// 2. WHO IT SUITS //////////
// Written as the situation rather than the trade, because the person reading
// this is checking whether their own week contains that moment.

const SUITS = [
  ["Jewellers who do not buy scrap", "You are already being asked. Right now the answer is a shrug."],
  ["Estate sale and clearance companies", "Every house has a drawer, and it is rarely worth your own time to handle."],
  ["Antique dealers and auctioneers", "The broken and unmarked pieces that never make a lot."],
  ["Pawn brokers", "The items you pass on, and the ones over your comfortable limit."],
  ["Coin shops", "Scrap jewellery, dental gold and sterling flatware, none of which is your trade."],
  ["Anyone with an audience", "A newsletter, a channel, a forum. The tools are free to embed either way."],
];

////////// 3. TERMS, PLAINLY //////////

const termsFor = (supply) => [
  {
    icon: Dollar,
    title: "You are paid per completed purchase",
    body: `You earn ${rateLine(supply)}. Not per mailer taken and not per enquiry: when somebody you sent accepts an offer and we pay them, you are paid too, at the rate on this page on the day their order arrives.`,
  },
  {
    icon: Shield,
    title: "You never touch the metal",
    body: "You hand somebody a kit. You do not weigh anything, value anything, quote anything or take anything into your possession. That is deliberate: it keeps you out of a licensing question you did not ask for, and it means nothing sitting in your shop is ever ours.",
  },
  {
    icon: Truck,
    title: `The stand and the first ${supply.startingMailers} mailers are free`,
    body: `The display, ${supply.startingMailers} mailers, the booklets and the postage to send them to you, at no cost. After that, more mailers are ${price(supply.mailerPrice)} each, which is what they cost us to make and post. There is no fee to join, no minimum, no exclusivity, and no contract that stops you working with anybody else. Ask us to stop and we stop.`,
  },
  {
    icon: Note,
    title: "The rate is set by us, and fixed the moment an order arrives",
    body: "We can change the rate, and we will not do it quietly: a change is published here and only applies to orders that come in afterwards. Anything already on its way to us pays what it was going to pay when it arrived. Commission is a share of what the customer is actually paid, so an order that is cancelled or returned earns nothing.",
  },
];

////////// 4. THE ENQUIRY //////////
// Composed into the message of an ordinary contact submission. The extra fields
// are here because they are what we would otherwise have to ask for in a reply,
// and a second email costs more than a third box on a form.

const KINDS = [
  { value: "Jeweller", label: "Jeweller" },
  { value: "Estate sale or clearance", label: "Estate sale or clearance" },
  { value: "Antiques or auction", label: "Antiques or auction" },
  { value: "Pawn broker", label: "Pawn broker" },
  { value: "Coin shop", label: "Coin shop" },
  { value: "Online audience", label: "Website, newsletter or channel" },
  { value: "Something else", label: "Something else" },
];

function ApplyForm() {
  const [sent, setSent] = useState(null);
  const form = useForm({
    name: "", business: "", kind: "", email: "", phone: "", place: "", note: "", website: "",
  });

  const submit = form.submit(async (values) => {
    const message = [
      `Business: ${values.business || "not given"}`,
      `Kind: ${values.kind || "not given"}`,
      `Where: ${values.place || "not given"}`,
      "",
      values.note || "No note.",
    ].join("\n");

    const result = await publicApi.contact({
      name: values.name,
      email: values.email,
      phone: values.phone || undefined,
      subject: `Affiliate enquiry from ${values.business || values.name}`,
      message,
      website: values.website,
    });

    setSent(result);
    return result;
  });

  if (sent) {
    return (
      <Panel className="p-6 sm:p-7">
        <p className="flex items-center gap-2.5 display display-sm text-ink">
          <Check size={20} className="text-positive" />
          Got it
        </p>
        <p className="hint mt-3 leading-relaxed measure">
          {sent.message || "Thanks. We answer everything within one business day."}
        </p>
        <p className="hint mt-3 leading-relaxed measure">
          A person will call or email you to set up your code and, if you asked for one, the
          display. If it turns out not to be worth your while, say so and nothing else happens.
        </p>
      </Panel>
    );
  }

  return (
    <Panel className="p-5 sm:p-6 md:p-7">
      <form onSubmit={submit} noValidate>
        <p className="eyebrow">Ask about it</p>
        <h2 className="display display-md text-ink mt-2">Tell us where the display would go.</h2>
        <p className="hint mt-2.5 leading-relaxed measure">
          No form fees and nothing to sign here. This is how a conversation starts, and you can end
          it on the first call.
        </p>

        <div className="grid gap-4 sm:grid-cols-2 mt-6">
          <Input {...form.field("name")} label="Your name" autoComplete="name" required />
          <Input {...form.field("business")} label="Business name" autoComplete="organization" />
          <Select {...form.field("kind")} label="What you do" options={KINDS} placeholder="Choose one" />
          <Input {...form.field("place")} label="Town and state" placeholder="Prescott, MI" autoComplete="address-level2" />
          <Input {...form.field("email")} type="email" label="Email" autoComplete="email" required />
          <Input {...form.field("phone")} type="tel" label="Phone" autoComplete="tel" />
        </div>

        <Textarea
          {...form.field("note")}
          label="Anything worth knowing"
          rows={3}
          wrapClassName="mt-4"
          hint="Roughly how often somebody asks you about selling gold is the useful thing to say."
        />

        {/* The honeypot. Not visible, never focusable, never announced. */}
        <input
          {...form.field("website")}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute w-px h-px -left-[9999px] opacity-0"
        />

        {form.message && (
          <Alert tone={form.message.kind === "error" ? "error" : "info"} className="mt-4">
            {form.message.text}
          </Alert>
        )}

        <Button type="submit" loading={form.busy} className="mt-5 w-full sm:w-auto">
          Send it <ArrowRight size={16} />
        </Button>

        <p className="fineprint mt-4 text-[12.5px] text-faint leading-relaxed">
          We use this to reply and nothing else. No list, no sequence, and we do not pass it on.
        </p>
      </form>
    </Panel>
  );
}

////////// 5. THE PAGE //////////

export default function WorkWithUs() {
  // Two numbers, quoted in four places on this page. Fetched rather than typed,
  // so changing what a restock costs is a settings change and not a deploy.
  const { data: settings } = useAsync(() => publicApi.settings(), []);
  const supply = partnerTerms(settings);
  const ways = waysFor(supply);
  const terms = termsFor(supply);

  return (
    <>
      <Seo
        title="Work with us: the affiliate programme"
        path="/work-with-us"
        description="Earn a commission for sending us gold and silver sellers. A free countertop display and a free stack of mailers to start, paid on completed purchases. No fee and no exclusivity."
      />
      <JsonLd data={breadcrumbSchema([{ label: "Home", to: "/" }, { label: "Work with us", to: "/work-with-us" }])} />

      <PageHead
        eyebrow="Work with us"
        title="Get paid for the customers you already turn away."
        lede="If people ask you about selling old gold and you have no good answer for them, we will give you one. Put our display on your counter, hand out the kits, and take a commission on anything that comes of it."
        art={() => (
          <Figure
            src={KIT.standStocked}
            alt="The countertop display stocked with a mailer and the booklets"
            sizes="(min-width: 1024px) 26vw, 60vw"
            className="max-w-[15rem] ml-auto"
            priority
          />
        )}
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/affiliate-signup" className="btn btn-primary">Apply to join <ArrowRight size={16} /></Link>
          <a href="#apply" className="btn btn-quiet">Talk to a person first</a>
        </div>
      </PageHead>

      <div className="shell pb-14 md:pb-20">
        {/* ---- the two ways ---- */}
        <StaggerBoxed
          as="div"
          step={90}
          className="grid gap-px bg-rule border border-rule md:grid-cols-2"
          itemClassName="bg-paper h-full"
        >
          {ways.map(({ icon: Glyph, title, body, aside }) => (
            <div key={title} className="bg-paper p-6 sm:p-7 md:p-8 h-full flex flex-col">
              <Glyph size={20} className="text-bronze mb-4" />
              <h2 className="display display-md text-ink">{title}</h2>
              <p className="hint mt-3 leading-relaxed flex-1">{body}</p>
              <p className="mt-4 pt-4 border-t border-rule-soft text-[13px] font-semibold text-bronze-dark">{aside}</p>
            </div>
          ))}
        </StaggerBoxed>

        {/* ---- the display, shown properly ---- */}
        <section className="mt-14 md:mt-20">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-14 items-center">
            <Reveal>
              <p className="eyebrow mb-3">What sits on the counter</p>
              <h2 className="display display-lg text-ink">A stand, and {supply.startingMailers} kits to start.</h2>
              <p className="lede mt-4 measure text-muted">
                It is about the size of a card reader. It holds the mailers, the booklet
                that explains how we work, and a metals guide, and it has a code on it that takes
                somebody straight to the form on their phone.
              </p>
              <p className="hint mt-4 leading-relaxed measure">
                The stand and the first {supply.startingMailers} mailers cost you nothing. When it runs low
                you tell us and more turn up, at {price(supply.mailerPrice)} a mailer, which is what they
                cost us to make and post. You never pay for the stand or the booklets. If you want
                it gone, we collect it or you bin it, and that is the end of the arrangement.
              </p>
            </Reveal>

            <Reveal delay={120} className="grid grid-cols-3 gap-4 sm:gap-6 items-end">
              <Figure
                src={KIT.stand}
                alt="The countertop display, empty, from the front"
                sizes="(min-width: 1024px) 18vw, 30vw"
                halo={false}
              />
              <Figure
                src={KIT.standMailers}
                alt="The display filled with mailers"
                sizes="(min-width: 1024px) 18vw, 30vw"
              />
              <Figure
                src={KIT.standBack}
                alt="The display from behind, loaded with mailers"
                sizes="(min-width: 1024px) 18vw, 30vw"
                halo={false}
              />
            </Reveal>
          </div>
        </section>

        {/* ---- terms ---- */}
        <section className="mt-14 md:mt-20">
          <Reveal>
            <p className="eyebrow mb-3">The arrangement</p>
            <h2 className="display display-lg text-ink max-w-2xl">Four things, and none of them are buried.</h2>
          </Reveal>

          <StaggerBoxed
            as="div"
            step={90}
            className="grid gap-px bg-rule border border-rule mt-8 lg:grid-cols-2"
            itemClassName="bg-paper h-full"
          >
            {terms.map(({ icon: Glyph, title, body }) => (
              <div key={title} className="bg-paper p-6 sm:p-7 h-full">
                <Glyph size={20} className="text-bronze mb-4" />
                <h3 className="display display-sm text-ink">{title}</h3>
                <p className="hint mt-3 leading-relaxed">{body}</p>
              </div>
            ))}
          </StaggerBoxed>

          <p className="hint mt-5 measure-wide leading-relaxed">
            The rate today is {rateLine(supply)}. We set it and can change it, and a change only
            ever applies to orders that arrive after it, never to one already on its way to us.
            The full terms come with the application.
          </p>
        </section>

        {/* ---- who it suits ---- */}
        <section className="mt-14 md:mt-20">
          <Reveal>
            <p className="eyebrow mb-3">Who this works for</p>
            <h2 className="display display-lg text-ink">You are probably already being asked.</h2>
          </Reveal>

          <StaggerBoxed
            as="dl"
            step={70}
            className="grid gap-px bg-rule border border-rule mt-8 sm:grid-cols-2 lg:grid-cols-3"
            itemClassName="bg-paper h-full"
          >
            {SUITS.map(([who, why]) => (
              <div key={who} className="bg-paper p-5 sm:p-6 h-full">
                <dt className="font-semibold text-[15px] text-ink">{who}</dt>
                <dd className="hint mt-1.5 leading-relaxed">{why}</dd>
              </div>
            ))}
          </StaggerBoxed>
        </section>

        {/* ---- apply ---- */}
        <section id="apply" className="mt-14 md:mt-20 scroll-mt-24 grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] lg:gap-12 items-start">
          <div>
            <Reveal as="h2" className="display display-lg text-ink">Two ways in.</Reveal>
            <p className="lede mt-4 measure-wide text-muted">
              Apply and a person reads it, usually within one business day. Or tell us a little here
              and we will call you first. Neither commits you to anything and neither costs anything.
            </p>

            {/* The application, given the weight. Somebody who has read this far
                has decided, and a three minute form they can finish now beats a
                phone call they have to wait for. */}
            <Panel className="p-5 sm:p-6 mt-6">
              <p className="eyebrow">The application</p>
              <p className="text-[14.5px] leading-relaxed text-muted mt-2">
                About three minutes. You get a code and a link of your own, and a display stand with
                mailers and booklets if you have a counter to put it on.
              </p>
              <div className="flex flex-wrap gap-3 mt-4">
                <Link to="/affiliate-signup" className="btn btn-primary">Apply now <ArrowRight size={16} /></Link>
                <Link to="/affiliate-portal" className="btn btn-quiet">Already signed up?</Link>
              </div>
            </Panel>

            <p className="hint mt-5 leading-relaxed measure">
              Every application starts as pending and a person approves it, so your code does nothing
              until we have spoken. If you would rather just talk, the number is on the{" "}
              <Link to="/contact" className="underline decoration-bronze-soft hover:decoration-bronze">contact page</Link>
              {" "}and a person answers it during business hours.
            </p>

            <Figure
              src={KIT.spread}
              alt="Mailers and booklets fanned out"
              sizes="(min-width: 1024px) 34vw, 80vw"
              className="mt-8 max-w-sm hidden lg:block"
            />
          </div>

          <div>
            <p className="eyebrow mb-3">Or have us call you</p>
            <ApplyForm />
          </div>
        </section>
      </div>

      <CtaBand
        title="Selling something yourself?"
        body="The kit is free for you too, and it works exactly the same way. Weighed, tested and quoted before you decide anything."
      />
    </>
  );
}
